import React, { useEffect, useRef } from 'react';
import { AlertTriangle, Save, X } from 'lucide-react';

/**
 * "Save" is about to replace a numbered version that already exists.
 *
 * Save and Save + differ only in whether the version number moves, and the two
 * sit next to each other in one menu. Picking the wrong one costs a version:
 * Save writes over `deck_v3.json` with no trace of what was there before, and
 * a deck is often the only copy of an afternoon's work.
 *
 * Shown only when there is genuinely something to replace. The first save of a
 * new deck creates `deck_v1.json` and gets no dialog — there is nothing to
 * warn about, and a confirmation for every save is a confirmation nobody reads.
 *
 * @param {object} props
 * @param {string} props.fileName - the file about to be replaced
 * @param {number} props.version - the version number being kept
 * @param {string} [props.where] - one line naming the folder, when known
 * @param {() => void} props.onConfirm
 * @param {() => void} props.onCancel
 */
export default function OverwriteVersionDialog({ fileName, version, where = '', onConfirm, onCancel }) {
  const confirmRef = useRef(null);

  // Focus lands on Overwrite because that is the action the user asked for;
  // Escape and Cancel are both one keystroke away, and the destructive step
  // still takes a deliberate press.
  useEffect(() => {
    confirmRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center overflow-y-auto bg-black/50 p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="overwrite-version-title"
      aria-describedby="overwrite-version-body"
    >
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start gap-4 rounded-t-2xl border-b border-gray-200 bg-amber-50 px-6 py-5">
          <div className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <AlertTriangle size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="overwrite-version-title" className="text-xl font-semibold text-gray-900">
              Overwrite saved version?
            </h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg p-2 text-gray-500 hover:bg-amber-100 hover:text-gray-700"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div id="overwrite-version-body" className="space-y-3 px-6 py-5 text-sm leading-relaxed text-gray-700">
          <p>
            You are about to overwrite <strong>Version {version}</strong> of this lesson plan.
          </p>
          <p>
            The existing <strong className="break-all">{fileName}</strong> file will be replaced with the
            current slide deck{where ? ` (${where})` : ''}. What is in it now cannot be recovered.
          </p>
          <p className="text-gray-500">
            Use <strong>Save +</strong> instead to keep this version and start a new numbered one.
          </p>
        </div>

        <div className="flex flex-col gap-3 rounded-b-2xl border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="order-last rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 sm:order-first sm:mr-auto"
          >
            Cancel
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-amber-700"
          >
            <Save size={16} />
            Overwrite
          </button>
        </div>
      </div>
    </div>
  );
}
